// apps/web/src/pages/admin/AuditLogPage.tsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { useAdminAudit } from '../../hooks/admin-stories';
import type { AuditAction, AuditLogFilters } from '../../types/story';

const ACTIONS: AuditAction[] = [
  'pipeline_create',
  'pipeline_add_member',
  'pipeline_synthesise',
  'admin_eject',
  'admin_merge',
  'admin_split',
  'admin_canonical_override',
  'admin_confidence_override',
  'admin_reset',
  'admin_resolve_tiers',
];

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function AuditLogPage() {
  const [filters, setFilters] = useState<AuditLogFilters>({ limit: 50 });
  const { data, isLoading } = useAdminAudit(filters);

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">Audit log</h1>
        <p className="text-sm text-muted-foreground">Every structural change to story clusters — pipeline and admin.</p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Story ID"
          value={filters.storyId ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, storyId: e.target.value || undefined, cursor: undefined }))}
          className="max-w-sm"
        />
        <Input
          placeholder="Actor ID"
          value={filters.actorId ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, actorId: e.target.value || undefined, cursor: undefined }))}
          className="max-w-xs"
        />
        <Select
          value={filters.action ?? 'all'}
          onValueChange={(v) => setFilters((f) => ({ ...f, action: v === 'all' ? undefined : (v as AuditAction), cursor: undefined }))}
        >
          <SelectTrigger className="w-64"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      <div className="overflow-x-auto rounded border">
        <table className="min-w-full text-xs">
          <thead className="bg-muted/40 uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Timestamp</th>
              <th className="px-3 py-2 text-left">Actor</th>
              <th className="px-3 py-2 text-left">Action</th>
              <th className="px-3 py-2 text-left">Story</th>
              <th className="px-3 py-2 text-left">Reason</th>
            </tr>
          </thead>
          <tbody>
            {(data?.entries ?? []).map((e) => (
              <tr key={e.id} className="border-t">
                <td className="px-3 py-1.5 tabular-nums">{formatTimestamp(e.createdAt)}</td>
                <td className="px-3 py-1.5">{e.actorType === 'system' ? 'system' : (e.actorId ?? '—')}</td>
                <td className="px-3 py-1.5 font-medium">{e.action}</td>
                <td className="px-3 py-1.5">
                  <Link to={`/admin/stories/${e.storyId}`} className="text-primary hover:underline">
                    {e.storyId.slice(0, 8)}…
                  </Link>
                </td>
                <td className="px-3 py-1.5 max-w-md truncate text-muted-foreground">{e.reason ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data?.nextCursor && (
        <Button variant="secondary" onClick={() => setFilters((f) => ({ ...f, cursor: data.nextCursor ?? undefined }))}>
          Load more
        </Button>
      )}
    </div>
  );
}
